import { InitBorrowerPricingProcessRequest, SidebarComponentDto } from "api/borrower";
import { borrowersAPI, pricingAPI } from "services/swagger-api";
import { MainBorrowerPricingSyncActions } from "../types";
import { SideMenuFilter, ApiFeesDto, ApiIdLabel } from "../../types";
import { PricingWatchdog } from "../pricingWatchdog";

// Constantes pour les clés de composants sidebar
const SIDEBAR_COMPONENT_KEYS = {
  PRICING_FILTERS: 'PRICING_FILTERS',
  PRICING_VARIABLE_AND_FEES: 'PRICING_VARIABLE_AND_FEES',
} as const;

// Type pour la config des filtres reçue de l'API
interface ApiPricingFilterConfig {
  type: string;
  title: string;
  items: Array<{ id: string; label: string }>;
}

// Type pour la config des variables et frais reçue de l'API
interface ApiVariablesAndFeesConfig {
  variables?: Array<unknown>;
  fees?: ApiFeesDto[];
  borrowersCommissions?: ApiIdLabel[];
  borrowerCommissionValue?: string;
}

/**
 * Parse les composants sidebar génériques de l'API et extrait les filtres
 */
const parseSidebarFilters = (sidebar: SidebarComponentDto[]): SideMenuFilter[] => {
  const filters: SideMenuFilter[] = [];

  for (const component of sidebar) {
    if (component.componentKey === SIDEBAR_COMPONENT_KEYS.PRICING_FILTERS) {
      // componentConfig est un tableau de PricingFilterConfig
      const filterConfigs = component.componentConfig as ApiPricingFilterConfig[];

      for (const filterConfig of filterConfigs) {
        filters.push({
          type: filterConfig.type,
          title: filterConfig.title,
          items: filterConfig.items.map(item => ({
            id: item.id || '',
            label: item.label || '',
            value: true // Initialisé à true pour tout afficher par défaut
          }))
        });
      }
    }
  }

  // Filtrer les catégories vides
  return filters.filter(filter => filter.items.length > 0);
};

/**
 * Parse les composants sidebar pour extraire les variables et frais
 */
const parseSidebarVariablesAndFees = (sidebar: SidebarComponentDto[]): ApiVariablesAndFeesConfig | undefined => {
  for (const component of sidebar) {
    if (component.componentKey === SIDEBAR_COMPONENT_KEYS.PRICING_VARIABLE_AND_FEES) {
      return component.componentConfig as ApiVariablesAndFeesConfig;
    }
  }
  return undefined;
};

/**
 * Parse la sidebar et dispatch les filtres/fees/commissions
 */
const parseSidebarData = (
  sidebar: SidebarComponentDto[] | null | undefined,
  guaranteesReferential: Array<unknown>,
  actions: MainBorrowerPricingSyncActions
) => {
  if (!sidebar) return;

  const mappedFilters = parseSidebarFilters(sidebar);
  const sidebarVariablesAndFees = parseSidebarVariablesAndFees(sidebar);
  const fees = sidebarVariablesAndFees?.fees;
  const borrowersCommissions = sidebarVariablesAndFees?.borrowersCommissions;
  const borrowerCommissionValue = sidebarVariablesAndFees?.borrowerCommissionValue;

  if (mappedFilters.length > 0 || guaranteesReferential.length > 0) {
    actions.setFetchBorrowerFunnelsReferential({
      filters: mappedFilters,
      fees,
      borrowersCommissions,
      borrowerCommissionValue,
      guaranteesReferential: guaranteesReferential as any
    })
  }
};

export const initBorrowerPricingProcessAsync = async ({ borrowerProjectId, emprunteurId }: InitBorrowerPricingProcessRequest,
  actions: MainBorrowerPricingSyncActions,
  pricingWatchdog: PricingWatchdog,
): Promise<void> => {
  pricingWatchdog.cancel()
  try {
    actions.setLoading(true)
    actions.setContractFunnelsLoading(true)
    actions.setError(null)

    // 1. Charger les données header (insured, loans, tabs) depuis l'init classique
    // const response = await borrowersAPI.initBorrowerPricingProcess({ borrowerProjectId, emprunteurId });
    // if (!response) return;
    //
    // const borrowerId = response.headerInfo.insuredWithLoans.insured.borrowerId;
    // const loanConfigurations = response.headerInfo.insuredWithLoans.loanConfigurations;
    //
    // actions.initBorrowerPricingProcess({
    //   borrowerProjectId,
    //   borrowerId,
    //   insured: response.headerInfo.insuredWithLoans.insured.person,
    //   tabs: response.headerInfo.tabs,
    //   loansConfigurations: loanConfigurations,
    // })
    //
    // // Appliquer les valeurs par défaut pour les champs nullish
    // const pendingLoanConfigs = loanConfigurations.map(config => ({
    //   ...config.insuranceConfig,
    //   loanId: config.loan.id,
    //   deductible: config.insuranceConfig.deductible || 90,
    //   quota: config.insuranceConfig.quota || 100,
    //   isPsyBuyback: config.insuranceConfig.isPsyBuyback ?? true,
    // }));
    // actions.updatePendingLoanConfigs(pendingLoanConfigs)

    if (!emprunteurId) {
      return
    }

    // 2. Appeler le v2/initial pour obtenir les contract funnels avec pricing asynchrone
    const request = {
      selectFunnelsBorrowerCommandDto: {
        riskId: emprunteurId,
      }
    };
    const v2Response = await pricingAPI.getBorrowerPricingInitialV2(request);

    if (v2Response) {
      const { contractFunnels, guarantees, sidebar, warningMessages, headerInfo } = v2Response;
      const guaranteesReferential = guarantees || [];
      if (headerInfo) {

        actions.initBorrowerPricingProcess({
          borrowerProjectId,
          borrowerId: emprunteurId,
          insured: headerInfo.insuredWithLoans.insured.person,
          tabs: headerInfo.tabs,
          loansConfigurations: headerInfo.insuredWithLoans.loanConfigurations,
        })

        const loanConfigurations = headerInfo.insuredWithLoans.loanConfigurations;
        const pendingLoanConfigs = loanConfigurations.map(config => ({
          ...config.insuranceConfig,
          loanId: config.loan.id,
          deductible: config.insuranceConfig.deductible || 90,
          quota: config.insuranceConfig.quota || 100,
          isPsyBuyback: config.insuranceConfig.isPsyBuyback ?? true,
        }));
        actions.updatePendingLoanConfigs(pendingLoanConfigs)
      }


      actions.updateGlobalWarningMessages(warningMessages || []);

      // Parser la sidebar (filtres, fees, commissions)
      parseSidebarData(sidebar, guaranteesReferential, actions);

      // Stocker les contract funnels (avec pricingStatus: Pending si async)
      if (contractFunnels) {
        actions.updateContractFunnels(contractFunnels);
        pricingWatchdog.arm(async () => (await pricingAPI.getBorrowerPricingInitialV2(request)).contractFunnels);
      }

    }
  } catch (error) {
    actions.setError(error instanceof Error ? error.message : 'Une erreur est survenue')
  } finally {
    actions.setLoading(false)
    actions.setContractFunnelsLoading(false)
  }
}
